import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { keys } from '../../api/keys'
import { Icon } from '../../ui/Icon'
import { Select } from '../../ui/Select'
import { useCategories } from '../categories/api'
import { useProduct, useProducts } from '../products/api'
import { LINK_KINDS, buildLink, parseLink, type LinkChoice, type LinkKind } from './model'

export interface StoryLinkProps {
  link: string
  disabled: boolean
  /** Что не так со ссылкой; пусто — всё в порядке. */
  error: string
  onChange: (link: string) => void
}

/** С какой длины строки искать товар: по одной букве совпадений слишком много. */
const MIN_QUERY = 2
const FOUND_MAX = 5

/**
 * Куда ведёт кнопка слайда: раздел приложения из списка, а не путь, который
 * надо знать. Категорию выбирают из списка, товар — поиском по названию;
 * незнакомый раздел или сайт — «свой адрес». Компонент держит выбор сам:
 * у категории без выбора ссылки ещё нет, а раздел уже выбран.
 */
export function StoryLink({ link, disabled, error, onChange }: StoryLinkProps) {
  const [choice, setChoice] = useState<LinkChoice>(() => parseLink(link))

  const update = (next: LinkChoice) => {
    setChoice(next)
    onChange(buildLink(next))
  }

  return (
    <div className="ui-field sto-link">
      <span className="ui-label" aria-hidden="true">
        Куда ведёт кнопка
      </span>
      <Select<LinkKind>
        label="Куда ведёт кнопка"
        value={choice.kind}
        disabled={disabled}
        options={LINK_KINDS}
        onChange={(kind) => {
          update({ kind, id: 0, custom: '' })
        }}
      />
      {choice.kind === 'category' ? (
        <CategoryPick
          id={choice.id}
          disabled={disabled}
          onPick={(id) => {
            update({ ...choice, id })
          }}
        />
      ) : null}
      {choice.kind === 'product' ? (
        <ProductPick
          id={choice.id}
          disabled={disabled}
          onPick={(id) => {
            update({ ...choice, id })
          }}
        />
      ) : null}
      {choice.kind === 'custom' ? (
        <input
          className="ui-input"
          aria-label="Свой адрес"
          placeholder="https://… или /путь в приложении"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          value={choice.custom}
          onChange={(e) => {
            update({ ...choice, custom: e.target.value })
          }}
        />
      ) : null}
      {error ? (
        <span className="ui-error" role="alert">
          <Icon name="i-alert" />
          {error}
        </span>
      ) : null}
    </div>
  )
}

interface PickProps {
  id: number
  disabled: boolean
  onPick: (id: number) => void
}

/** Категория из списка витрины. */
function CategoryPick({ id, disabled, onPick }: PickProps) {
  const categories = useCategories()
  if (categories.isPending) return <span className="ui-hint">Загружаем категории…</span>
  if (categories.isError) {
    return <span className="ui-hint">Категории не загрузились — выберите «Свой адрес»</span>
  }
  return (
    <Select<number>
      label="Категория"
      value={id}
      disabled={disabled}
      options={[
        { value: 0, label: 'Выберите категорию' },
        ...categories.data.map((c) => ({ value: c.id, label: c.name })),
      ]}
      onChange={onPick}
    />
  )
}

/**
 * Товар поиском по названию. Выбранный — строкой с названием и «Изменить»:
 * номер товара человеку ничего не говорит.
 */
function ProductPick({ id, disabled, onPick }: PickProps) {
  const [q, setQ] = useState('')
  const query = q.trim()
  const client = useQueryClient()
  const chosen = useProduct(id)
  const searching = query.length >= MIN_QUERY
  const found = useProducts({ q: query, pageSize: FOUND_MAX }, searching && id === 0)

  if (id > 0) {
    return (
      <div className="sto-link-chosen">
        <Icon name="i-pill" />
        <span className="sto-link-name">
          {chosen.data?.name ?? (chosen.isError ? `Товар №${id} не найден` : `Товар №${id}`)}
        </span>
        {disabled ? null : (
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            data-size="sm"
            onClick={() => {
              setQ('')
              onPick(0)
            }}
          >
            Изменить
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="sto-link-search">
      <div className="ui-search">
        <Icon name="i-search" />
        <input
          className="ui-input"
          type="search"
          aria-label="Найти товар"
          placeholder="Название товара"
          autoComplete="off"
          disabled={disabled}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
          }}
        />
      </div>
      {searching && found.isPending && !found.data ? <span className="ui-hint">Ищем…</span> : null}
      {searching && found.isError ? (
        <span className="ui-hint">Товары не загрузились — выберите «Свой адрес»</span>
      ) : null}
      {searching && found.data && !found.isPlaceholderData ? (
        found.data.items.length === 0 ? (
          <span className="ui-hint">Ничего не нашлось</span>
        ) : (
          <ul className="sto-link-found" aria-label="Найденные товары">
            {found.data.items.map((product) => (
              <li key={product.id}>
                <button
                  type="button"
                  onClick={() => {
                    // Название уже известно из поиска: строка выбора не мигает «Товар №…».
                    client.setQueryData(keys.product(product.id), product)
                    onPick(product.id)
                  }}
                >
                  <b>{product.name}</b>
                  {product.maker ? <span>{product.maker}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  )
}
